import Download from 'downloadjs'
import React, { useState, useContext, useEffect, KeyboardEvent } from 'react'
import ReactGA from 'react-ga'

import { API_GET_THUMB_UPLOAD_URL, API_GET_UPLOAD_URL, THUMBNAIL_SIZE } from './constants'
import { highQualityDownscale } from './util'
import ChannelSettings from './ChannelSettings'
import GraphContext, { GraphContextType } from './GraphContext'
import { attachConfigToUrl } from './GraphParamDefs'
import PrintContext, { getManifest, PrintContextType } from './PrintContext'
import UserContext, { UserContextType } from './UserContext'

type Props = { isShowAdvanced: boolean; onLoadFile: (file: File) => void }

const READY_TO_UPLOAD = 'upload'
const TOAST_MILLIS = 4000

const WheelActions = ({ isShowAdvanced, onLoadFile }: Props) => {
  const [uploadStatus, setUploadStatus] = useState(READY_TO_UPLOAD)
  const [downloadsClicked, setDownloadsClicked] = useState(0)
  const {
    userContext: { isShowControls, version },
  } = useContext(UserContext) as UserContextType
  const {
    graphContext: { graphRef: stageRef, graphConfig: config, isLog, graphControls },
    setIsLog,
  } = useContext(GraphContext) as GraphContextType
  const { printContext } = useContext(PrintContext) as PrintContextType
  const { pixelRatio, title, frameNumber } = printContext
  useEffect(() => {
    const handleKey = (event: unknown) => {
      const keyEvent = event as KeyboardEvent
      const uploadPrefix = keyEvent.altKey
        ? keyEvent.shiftKey
          ? ' JSON only'
          : ' thumbnail only'
        : ''
      setUploadStatus(`${READY_TO_UPLOAD}${uploadPrefix}`)
    }
    window.addEventListener('keydown', handleKey)
    window.addEventListener('keyup', handleKey)
    return () => {
      window.removeEventListener('keydown', handleKey)
      window.removeEventListener('keyup', handleKey)
    }
  })

  const handleLog = () => {
    setIsLog(!isLog)
  }
  const logExportEvent = () => {
    //  https://developers.google.com/analytics/devguides/collection/protocol/v1/parameters
    const label = getLink() // TODO: max chars 500
    const value = downloadsClicked + 1
    ReactGA.event({
      category: 'User',
      action: 'Tapped Download',
      label,
      value,
    })
    setDownloadsClicked(value)
  }

  const handleImport = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (files) {
      onLoadFile(files[0])
    }
  }

  const uploadJson = async () => {
    setUploadStatus('uploading json...')
    const multipolarJson = getManifest(printContext, graphControls, config, version)
    const filePath = `${config.formula}/${title}`
    const contentType = 'json'
    const apiUrl = `${API_GET_UPLOAD_URL}/${encodeURIComponent(filePath)}.${contentType}`
    const res = await fetch(apiUrl)
    const { signedUrl } = await res.json()
    const { ok } = await fetch(signedUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': contentType,
      },
      body: multipolarJson,
    })
    if (ok) {
      window.history.pushState({}, '', `/archive/${filePath}`)
    }
    return ok
  }

  const uploadThumbnail = () => {
    return new Promise<boolean>((resolve, reject) => {
      const { current } = stageRef
      if (current) {
        const sourceCanvas = current.toCanvas()
        const resizedCanvas = highQualityDownscale(sourceCanvas, THUMBNAIL_SIZE)
        resizedCanvas.toBlob(
          async (blob) => {
            if (!blob) {
              reject('failed to create thumbnail')
            }
            const filePath = `${config.formula}/${title}x${THUMBNAIL_SIZE}`
            const apiUrl = `${API_GET_THUMB_UPLOAD_URL}/${encodeURIComponent(filePath)}.webp`
            const res = await fetch(apiUrl)
            const { signedUrl } = await res.json()
            const { ok } = await fetch(signedUrl, {
              method: 'PUT',
              headers: {
                'Content-Type': 'image/webp',
              },
              body: blob,
            })
            resolve(ok)
          },
          'image/webp',
          0.9
        )
      }
    })
  }
  const handleUpload = async (event: React.MouseEvent) => {
    const isUploadJson = !(event.altKey && !event.shiftKey)
    const isUploadThumbmnail = !(event.altKey && event.shiftKey)
    let isContinue = true
    if (isUploadJson) {
      setUploadStatus('uploading json...')
      isContinue = await uploadJson()
      if (!isContinue) {
        setUploadStatus('json upload failed')
      }
    }
    if (isContinue && isUploadThumbmnail) {
      setUploadStatus('uploading thumbnail...')
      isContinue = await uploadThumbnail()
      if (!isContinue) {
        setUploadStatus('thumbnail upload failed')
      }
    }
    if (isContinue) {
      const isBoth = isUploadJson && isUploadThumbmnail
      const uploadPrefix = `${isUploadJson ? 'json' : ''}${isBoth ? ' and ' : ''}${
        isUploadThumbmnail ? 'thumbnail' : ''
      }`
      setUploadStatus(`${uploadPrefix} uploaded!`)
    }
    setTimeout(() => {
      setUploadStatus(READY_TO_UPLOAD)
    }, TOAST_MILLIS)
  }
  const handleExport = () => {
    const { current } = stageRef
    if (current) {
      const uri = current.toDataURL({ pixelRatio })
      var link = document.createElement('a')
      link.download = `${title}.png`
      link.href = uri
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      logExportEvent()
      const multipolarJson = getManifest(printContext, graphControls, config, version)
      Download(multipolarJson, `${title}.json`, 'text/plain')
    }
  }
  const getLink = () => {
    const { pathname, origin } = window.location
    if (pathname.length > 1) {
      const [, type, name] = pathname.split('/')
      return `${origin}/${type}/${name}/${frameNumber}`
    }
    // @ts-ignore
    const newUrl = new URL(window.location)
    attachConfigToUrl(newUrl, config, version)
    return newUrl.href
  }
  const link = getLink()
  return (
    <div className="wheelActions">
      {isShowAdvanced && isShowControls && <ChannelSettings />}
      {isShowAdvanced && isShowControls && (
        <div>
          <button onClick={handleLog}>{isLog ? 'logging' : 'log'}</button>
          <input type="file" onChange={handleImport} />
          <button onClick={handleUpload}>{uploadStatus}</button>
          <button onClick={handleExport}>download</button>
          <a href={link}>{title || 'untitled'}</a>
        </div>
      )}
    </div>
  )
}

export default WheelActions
